import { Box, Button, Checkbox, FooterBanner, Modal, TextInput } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { string } from "yup";

import style from "components/asset-management-system/asset-table.scss";

import testIds from "testIds.json";

interface ExportAssetsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onExport: (includeManifestDetails: boolean, emailTo?: string, emailSubject?: string) => void;
    sendEmail?: boolean;
    manifestExists?: boolean;
    allAssets?: boolean;
    assetCount?: number;
}

const ExportAssetsModal = (props: ExportAssetsDialogProps) => {
    const { t } = useTranslation();
    const [includeManifestDetails, setIncludeManifestDetails] = React.useState(props.manifestExists === true);
    const [emailTo, setEmailTo] = React.useState("");
    const [emailToError, setEmailToError] = React.useState<string | undefined>(undefined);
    const [emailSubject, setEmailSubject] = React.useState("");
    const [emailSubjectError, setEmailSubjectError] = React.useState<string | undefined>(undefined);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);

    React.useEffect(() => {
        if (props.isOpen) {
            setEmailTo("");
            setEmailSubject("");
            setEmailToError(undefined);
            setEmailSubjectError(undefined);
            setHasSubmitted(false);
            setIncludeManifestDetails(props.manifestExists === true);
        }
    }, [props.isOpen, props.manifestExists]);

    const FILE_FORMAT = "ZIP";
    const fileFormatLabel = props.sendEmail
        ? t("AssetManagement.sendViaEmail.attachmentType")
        : t("AssetManagement.exportAssets.fileFormat");

    const validateEmailTo = (value: string): string | undefined => {
        if (!value.trim()) {
            return t("AssetManagement.sendViaEmail.requiredTo");
        }
        if (!string().email().isValidSync(value)) {
            return t("AssetManagement.sendViaEmail.invalidEmail");
        }
        return undefined;
    };

    const validateEmailSubject = (value: string): string | undefined => {
        if (!value.trim()) {
            return t("AssetManagement.sendViaEmail.requiredSubject");
        }
        return undefined;
    };

    const handleSend = () => {
        if (props.sendEmail) {
            const toError = validateEmailTo(emailTo);
            const subjectError = validateEmailSubject(emailSubject);
            setEmailToError(toError);
            setEmailSubjectError(subjectError);
            if (toError ?? subjectError) {
                setHasSubmitted(true);
                return;
            }
        }
        props.onExport(
            includeManifestDetails,
            props.sendEmail ? emailTo : undefined,
            props.sendEmail ? emailSubject : undefined
        );
    };

    return (
        <Modal
            isOpen={props.isOpen}
            hideModal={props.onClose}
            title={
                props.sendEmail
                    ? t("AssetManagement.sendViaEmail.title")
                    : props.allAssets
                      ? t("AssetManagement.exportAllAssets")
                      : t("AssetManagement.exportAssets.title", { count: props.assetCount })
            }
            width={props.sendEmail ? "Md" : undefined}
            buttons={
                <Box gap={"Xs"}>
                    <Button
                        dataType={props.sendEmail ? "SEND_EMAIL" : "EXPORT"}
                        variant={"Primary"}
                        onClick={handleSend}
                        disabled={emailToError !== undefined || emailSubjectError !== undefined}
                        testId={testIds.common.dialog.primaryButton}
                    >
                        {props.sendEmail ? t("Common.send") : t("Common.export")}
                    </Button>
                    <Button variant={"Secondary"} onClick={props.onClose} testId={testIds.common.dialog.closeButton}>
                        {t("Common.cancel")}
                    </Button>
                </Box>
            }
            footerBanner={
                emailToError !== undefined || emailSubjectError !== undefined ? (
                    <FooterBanner text={t("Common.fixErrors")} />
                ) : undefined
            }
        >
            {props.sendEmail && (
                <Box columnLayout gap={"Sm"} marginBottom={"Sm"}>
                    <TextInput
                        label={t("AssetManagement.sendViaEmail.to")}
                        name={t("AssetManagement.sendViaEmail.to")}
                        value={emailTo}
                        onChange={(e) => {
                            setEmailTo(e.target.value);
                            if (hasSubmitted) {
                                setEmailToError(validateEmailTo(e.target.value));
                            }
                        }}
                        error={emailToError}
                        dataType="EMAIL_TO"
                    />
                    <TextInput
                        label={t("AssetManagement.sendViaEmail.subject")}
                        name={t("AssetManagement.sendViaEmail.subject")}
                        value={emailSubject}
                        onChange={(e) => {
                            setEmailSubject(e.target.value);
                            if (hasSubmitted) {
                                setEmailSubjectError(validateEmailSubject(e.target.value));
                            }
                        }}
                        error={emailSubjectError}
                        dataType="EMAIL_SUBJECT"
                    />
                </Box>
            )}
            <Box gap="Sm" verticalAlignment="Center">
                <span className={style.fieldLabel}>{fileFormatLabel}</span>
                <span>{FILE_FORMAT}</span>
            </Box>
            {props.manifestExists && (
                <Checkbox
                    dataType={"EXPORT_MANIFEST"}
                    label={t("AssetManagement.exportAssets.manifestDetails")}
                    name="includeManifestDetails"
                    checked={includeManifestDetails}
                    value="includeManifestDetails"
                    hideLabelSection={!props.sendEmail}
                    fieldLabel={props.sendEmail ? t("AssetManagement.sendViaEmail.include") : undefined}
                    onChange={() => setIncludeManifestDetails(!includeManifestDetails)}
                />
            )}
        </Modal>
    );
};
export default ExportAssetsModal;
